import { useEffect } from "react";
import { Checkbox, FieldLabel, TextInput } from "../_components/AdminShell";
import { AdminMenuSelect } from "@/components/admin/AdminMenuSelect";
import { StringChipsEditor } from "../../../components/admin/StringChipsEditor";
import { parseChoiceLines } from "../../../lib/formBuilder/choiceLines";
import { networkLeaderPresetsForCity } from "../../../lib/formBuilder/networkLeaderPresets";
import { resolveFieldType } from "../../../lib/forms/normalizeFieldType";
import type { FormBuilderField } from "../../../types/formBuilder";
import { fieldVisibilityCaption } from "./VisibilityConditionEditor";
import { CitySourceKeyPicker } from "./CitySourceKeyPicker";
import { ChoicesUrlFields } from "./ChoicesUrlFields";

/** Ключи источника для choices_by: value|label → value, иначе сама строка. */
function sourceKeysFromField(source: FormBuilderField | undefined): Array<{
  key: string;
  label: string;
}> {
  if (!source) return [];
  if (resolveFieldType(source.type) === "city") {
    return source.cityIds.map((id) => {
      const key = String(id);
      return {
        key,
        label: source.cityLabels[key] || `id ${id}`,
      };
    });
  }

  return source.choicesText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const split = line.indexOf("|");
      if (split <= 0) return { key: line, label: line };
      return {
        key: line.slice(0, split).trim(),
        label: line.slice(split + 1).trim() || line.slice(0, split).trim(),
      };
    })
    .filter((item) => item.key);
}

function selectedNamesForKey(
  mapText: Record<string, string>,
  key: string,
): string[] {
  return parseChoiceLines(mapText[key] ?? "");
}

function writeNames(
  mapText: Record<string, string>,
  key: string,
  names: string[],
): Record<string, string> {
  const next = { ...mapText };
  const unique = parseChoiceLines(names.join("\n"));
  if (unique.length === 0) delete next[key];
  else next[key] = unique.join("\n");
  return next;
}

export function ChoicesByMapEditor({
  field,
  otherFields,
  sourceField,
  choicesByKey,
  cityKeyLabels,
  onChange,
  onChoicesByKeyChange,
  onCityKeyLabelsChange,
}: {
  field: FormBuilderField;
  otherFields: FormBuilderField[];
  sourceField: FormBuilderField | undefined;
  choicesByKey: string;
  cityKeyLabels: Record<string, string>;
  onChange: (patch: Partial<FormBuilderField>) => void;
  onChoicesByKeyChange: (key: string) => void;
  onCityKeyLabelsChange: (
    update:
      | Record<string, string>
      | ((prev: Record<string, string>) => Record<string, string>),
  ) => void;
}) {
  const sourceIsCity =
    Boolean(sourceField) && resolveFieldType(sourceField?.type) === "city";
  const sourceKeys = sourceKeysFromField(sourceField);
  const mappedCityKeys = Object.keys(field.choicesByMapText).filter((key) =>
    (field.choicesByMapText[key] ?? "").trim(),
  );
  const activeKey =
    choicesByKey ||
    sourceKeys[0]?.key ||
    mappedCityKeys[0] ||
    Object.keys(field.choicesByMapText)[0] ||
    "";

  useEffect(() => {
    if (activeKey && !choicesByKey) onChoicesByKeyChange(activeKey);
  }, [activeKey, choicesByKey, onChoicesByKeyChange]);

  const activeLabel =
    cityKeyLabels[activeKey] ||
    sourceKeys.find((item) => item.key === activeKey)?.label ||
    (activeKey ? `id ${activeKey}` : "");

  const presets = sourceIsCity
    ? networkLeaderPresetsForCity(activeKey, activeLabel)
    : [];
  const selected = selectedNamesForKey(field.choicesByMapText, activeKey);
  const selectedSet = new Set(selected);
  const extras = selected.filter((name) => !presets.includes(name));

  const setSelected = (names: string[]) => {
    if (!activeKey) return;
    onChange({
      choicesByMapText: writeNames(field.choicesByMapText, activeKey, names),
    });
  };

  const togglePreset = (name: string, checked: boolean) => {
    if (checked) {
      setSelected([
        ...presets.filter((item) => selectedSet.has(item) || item === name),
        ...extras,
      ]);
      return;
    }
    setSelected(selected.filter((item) => item !== name));
  };

  const cityTabs =
    sourceIsCity && sourceKeys.length > 0 ? (
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Город">
        {sourceKeys.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={activeKey === item.key}
            onClick={() => onChoicesByKeyChange(item.key)}
            className={[
              "rounded-md px-2 py-1 font-onest text-[11px] font-medium transition-colors",
              activeKey === item.key
                ? "bg-brand-100 text-brand-700"
                : "bg-white text-slate-600 hover:bg-slate-100",
            ].join(" ")}
          >
            {item.label}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-2.5">
      <div>
        <FieldLabel size="compact">Поле-источник</FieldLabel>
        <AdminMenuSelect
          ariaLabel="Поле-источник"
          value={field.choicesByField}
          placeholder="— выберите —"
          emptyLabel="— выберите —"
          options={[
            { value: "", label: "— выберите —" },
            ...otherFields.map((item) => ({
              value: item.name,
              label: fieldVisibilityCaption(item),
            })),
          ]}
          onChange={(choicesByField) => {
            onChange({ choicesByField, choicesByMapText: {} });
            onChoicesByKeyChange("");
            onCityKeyLabelsChange({});
          }}
        />
      </div>

      {field.choicesByField ? (
        <>
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() =>
                onChange({
                  choicesByUrl: "",
                  choicesByPath: "",
                  choicesByItem: "",
                  choicesByValue: "",
                })
              }
              className={[
                "flex-1 rounded-md px-2 py-1 font-onest text-[11px] font-medium",
                !field.choicesByUrl.trim()
                  ? "bg-slate-100 text-ink"
                  : "text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              Заготовки
            </button>
            <button
              type="button"
              onClick={() =>
                onChange({
                  choicesByMapText: {},
                  choicesByUrl: field.choicesByUrl.trim() || "https://",
                })
              }
              className={[
                "flex-1 rounded-md px-2 py-1 font-onest text-[11px] font-medium",
                field.choicesByUrl.trim()
                  ? "bg-slate-100 text-ink"
                  : "text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              Из API
            </button>
          </div>

          {cityTabs}

          {sourceIsCity && sourceKeys.length === 0 ? (
            <CitySourceKeyPicker
              keys={Array.from(
                new Set([
                  ...mappedCityKeys,
                  ...Object.keys(cityKeyLabels),
                  ...(activeKey ? [activeKey] : []),
                ]),
              ).map((key) => ({
                key,
                label: cityKeyLabels[key] || `id ${key}`,
              }))}
              activeKey={activeKey}
              onActivate={onChoicesByKeyChange}
              onSelect={(key, label) => {
                onChoicesByKeyChange(key);
                if (key && label) {
                  onCityKeyLabelsChange((prev) => ({ ...prev, [key]: label }));
                }
                if (key && !(key in field.choicesByMapText)) {
                  onChange({
                    choicesByMapText: { ...field.choicesByMapText, [key]: "" },
                  });
                }
              }}
              onRemove={(key) => {
                const nextMap = { ...field.choicesByMapText };
                delete nextMap[key];
                onChange({ choicesByMapText: nextMap });
                onCityKeyLabelsChange((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                });
                if (activeKey === key) {
                  onChoicesByKeyChange(Object.keys(nextMap)[0] || "");
                }
              }}
            />
          ) : null}

          {!sourceIsCity && sourceKeys.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {sourceKeys.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onChoicesByKeyChange(item.key)}
                  className={[
                    "rounded-md px-2 py-1 font-onest text-[11px] font-medium transition-colors",
                    activeKey === item.key
                      ? "bg-brand-100 text-brand-700"
                      : "bg-white text-slate-600 hover:bg-slate-100",
                  ].join(" ")}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}

          {!sourceIsCity && sourceKeys.length === 0 ? (
            <div>
              <FieldLabel size="compact">Ключ источника</FieldLabel>
              <TextInput
                size="compact"
                value={activeKey}
                placeholder="значение"
                onChange={(key) => onChoicesByKeyChange(key.trim())}
                className="font-mono"
              />
              <p className="mt-1 font-onest text-[11px] text-slate-500">
                Укажите value поля-источника
              </p>
            </div>
          ) : null}

          {field.choicesByUrl.trim() ? (
            <div className="space-y-2">
              <p className="font-onest text-[11px] text-slate-500">
                В ссылке {"`{value}`"} подставится id источника. На форме список
                грузится только после выбора города.
              </p>
              <ChoicesUrlFields
                url={
                  field.choicesByUrl.trim() === "https://"
                    ? ""
                    : field.choicesByUrl
                }
                path={field.choicesByPath}
                item={field.choicesByItem}
                value={field.choicesByValue}
                source={activeKey || undefined}
                urlPlaceholder="https://example.com/api/leaders?city={value}"
                onUrlChange={(choicesByUrl) =>
                  onChange({
                    choicesByUrl: choicesByUrl.trim() || "https://",
                  })
                }
                onPathChange={(choicesByPath) => onChange({ choicesByPath })}
                onItemChange={(choicesByItem) => onChange({ choicesByItem })}
                onValueChange={(choicesByValue) =>
                  onChange({ choicesByValue })
                }
              />
            </div>
          ) : activeKey ? (
            <div className="space-y-2">
              <FieldLabel size="compact">
                Варианты для «
                {sourceIsCity
                  ? activeLabel
                  : (sourceKeys.find((item) => item.key === activeKey)?.label ??
                    activeKey)}
                »
              </FieldLabel>

              {presets.length > 0 ? (
                <div className="grid gap-1.5">
                  {presets.map((name) => (
                    <Checkbox
                      key={name}
                      id={`choices-by-${field.clientId}-${activeKey}-${name}`}
                      size="compact"
                      checked={selectedSet.has(name)}
                      onChange={(checked) => togglePreset(name, checked)}
                      label={name}
                    />
                  ))}
                </div>
              ) : null}

              {extras.length > 0 || presets.length > 0 ? (
                <p className="font-onest text-[11px] text-slate-500">
                  {presets.length > 0
                    ? "Отметьте из заготовки или введите имя, если человека нет в списке."
                    : "Введите имена — в форму уйдут только отмеченные."}
                </p>
              ) : (
                <p className="font-onest text-[11px] text-slate-500">
                  Для этого города нет заготовки. Добавьте имена вручную.
                </p>
              )}

              <StringChipsEditor
                values={extras}
                placeholder="Нет в списке — имя и Enter"
                onChange={(nextExtras) => {
                  setSelected([
                    ...presets.filter((name) => selectedSet.has(name)),
                    ...nextExtras,
                  ]);
                }}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
